import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import ClearIcon from "@mui/icons-material/Clear";
import { Search } from "lucide-react";

const OutstandingTable = ({ tableData }) => {
  const [searchAccountTerm, setSearchAccountTerm] = useState("");
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [viewMore, setViewMore] = useState(false);
  const [filteredTableData, setFilteredTableData] = useState(tableData);
  // State to track expanded row descriptions
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    if (!searchAccountTerm) {
      setFilteredTableData(tableData || []);
      return;
    }

    const term = searchAccountTerm.toLowerCase();
    const searchedTableData = tableData?.filter((row) =>
      Object.values(row).some(
        (val) => val != null && String(val).toLowerCase().includes(term)
      )
    );

    setFilteredTableData(searchedTableData);
  }, [searchAccountTerm, tableData]);

  const clearSearch = () => {
    setSearchAccountTerm("");
  };

  const toggleExpandDescription = (idx) => {
    setExpandedRows((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  const debitTotal = filteredTableData?.reduce(
    (sum, row) => sum + Number(row.DEBIT || 0),
    0
  );

  const creditTotal = filteredTableData?.reduce(
    (sum, row) => sum + Number(row.CREDIT || 0),
    0
  );

  return (
    <div>
      {/* Header Controls */}
      <div className="flex items-center justify-between gap-4 bg-gray-200  p-3 rounded-t-xl  shadow-md">
        <h2 className="text-lg font-bold uppercase tracking-wide gray-700 whitespace-nowrap">
          Outstanding Statement
        </h2>

        {/* Search Bar */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2  w-4 h-4" />
          <input
            className="w-full pl-9 pr-8 py-1.5 text-sm rounded-lg border border-slate-700  text-gray-900 placeholder-slate-400 outline-none transition-all "
            type="text"
            placeholder="Search accounts or invoices..."
            value={searchAccountTerm}
            onChange={(e) => setSearchAccountTerm(e.target.value)}
          />
          {searchAccountTerm !== "" && (
            <button
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
              onClick={clearSearch}
            >
              <ClearIcon style={{ fontSize: "16px" }} />
            </button>
          )}
        </div>

        {/* Right Controls */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-slate-800 border border-slate-700">
            <span className="text-xs uppercase font-medium text-slate-400">
              Balance:
            </span>
            <span className="text-sm font-bold font-mono text-emerald-400">
              {(debitTotal - creditTotal).toFixed(2)}
            </span>
          </div>

          <button
            onClick={() => setViewMore(!viewMore)}
            className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg text-xs tracking-wide uppercase shadow-sm transition-all active:scale-95 whitespace-nowrap"
          >
            {viewMore ? "View Less" : "View More"}
          </button>
        </div>
      </div>

      {/* Scrollable Table */}
      <div className="overflow-y-auto max-h-[72vh] border border-slate-200 rounded-b-lg shadow-sm bg-white">
        <table className="min-w-full border-separate border-spacing-0 text-slate-700">
          <thead className="bg-slate-100/90 backdrop-blur sticky top-0  border-b border-slate-200 [&_th]:whitespace-nowrap">
            <tr className="text-xs uppercase font-semibold tracking-wider text-slate-600">
              {/* <th className="px-3 py-2.5 text-left border-b border-r border-slate-200 w-[200px]">
                Name
              </th>
              <th className="px-3 py-2.5 text-left border-b border-r border-slate-200 w-[110px]">
                Account
              </th> */}
              <th className="px-3 py-2.5 text-left border-b border-r border-slate-200 w-[125px]">
                Invoice Date
              </th>
              <th className="px-3 py-2.5 text-left border-b border-r border-slate-200 w-[110px]">
                Invoice
              </th>
              <th className="px-3 py-2.5 text-left border-b border-r border-slate-200">
                Description
              </th>
              <th className="px-3 py-2.5 text-left border-b border-r border-slate-200 w-[120px]">
                Salesperson
              </th>
              <th className="px-3 py-2.5 text-right border-b border-r border-slate-200 w-[120px] bg-sky-50/50 text-sky-900">
                Debit
              </th>
              <th className="px-3 py-2.5 text-right border-b border-r border-slate-200 w-[120px] bg-emerald-50/50 text-emerald-900">
                Credit
              </th>
              <th className="px-3 py-2.5 text-right border-b border-slate-200 w-[130px] bg-slate-200/40">
                Balance
              </th>

              {viewMore && (
                <>
                  <th className="px-3 py-2.5 text-left border-b border-r border-slate-200 w-[250px]">
                    Address
                  </th>
                  <th className="px-3 py-2.5 text-right border-b border-r border-slate-200">
                    CRLimit
                  </th>
                  <th className="px-3 py-2.5 text-left border-b border-r border-slate-200">
                    LPO
                  </th>
                  <th className="px-3 py-2.5 text-left border-b border-slate-200">
                    TRN
                  </th>
                </>
              )}
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 text-sm">
            {filteredTableData?.length > 0 ? (
              filteredTableData.map((row, idx) => {
                const debit = Number(row.DEBIT || 0);
                const credit = Number(row.CREDIT || 0);
                const bal = Number(row.BALANCE || 0);
                const isExpanded = !!expandedRows[idx];

                return (
                  <tr
                    key={idx}
                    className={`hover:bg-slate-100/70 transition-colors even:bg-slate-50/50 ${
                      selectedLeads.includes(row.Job_id) ? "bg-blue-50/80" : ""
                    }`}
                  >
                    {/* Name */}
                    {/* <td
                      className="px-3 py-2 text-xs font-semibold text-slate-800 border-r border-slate-100 max-w-[200px] truncate"
                      title={row.NAME}
                    >
                      {row.NAME || "-"}
                    </td> */}

                    {/* Account */}
                    {/* <td className="px-3 py-2 text-xs font-mono text-slate-600 border-r border-slate-100 whitespace-nowrap">
                      {row.ACCOUNT || "-"}
                    </td> */}

                    {/* Invoice Date */}
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600 border-r border-slate-100 font-medium">
                      {row.INVDATE ? dayjs(row.INVDATE).format("DD-MMM-YYYY") : "-"}
                    </td>

                    {/* Invoice */}
                    <td className="px-3 py-2 text-xs font-mono text-slate-600 border-r border-slate-100 whitespace-nowrap">
                      {row.INV || "-"}
                    </td>

                    {/* Interactive Description */}
                    <td
                      onClick={() => toggleExpandDescription(idx)}
                      className="px-3 py-2 text-slate-800 border-r border-slate-100 cursor-pointer select-none group"
                      title={`Click to view: ${row.DESCRIPTION}`}
                    >
                      <div
                        className={`transition-all ${
                          isExpanded
                            ? "whitespace-normal break-words max-w-[200px]"
                            : "max-w-[200px] truncate"
                        } group-hover:text-blue-600`}
                      >
                        {row.DESCRIPTION || "-"}
                      </div>
                    </td>

                    {/* Salesperson */}
                    <td className="px-3 py-2 text-xs text-slate-600 border-r border-slate-100 max-w-[120px] truncate">
                      {row.SALESPERSON || "-"}
                    </td>

                    {/* Pastel Debit Column */}
                    <td className="px-3 py-2 text-right font-mono tabular-nums border-r border-slate-100 bg-sky-50/30 text-sky-800 font-medium">
                      {debit > 0 ? debit.toFixed(2) : ""}
                    </td>

                    {/* Pastel Credit Column */}
                    <td className="px-3 py-2 text-right font-mono tabular-nums border-r border-slate-100 bg-emerald-50/30 text-emerald-800 font-medium">
                      {credit > 0 ? credit.toFixed(2) : ""}
                    </td>

                    {/* Balance */}
                    <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold border-slate-100 text-slate-800 bg-slate-50/60">
                      {bal.toFixed(2)}
                    </td>

                    {/* Expanded Columns */}
                    {viewMore && (
                      <>
                        <td
                          className="px-3 py-2 border-r border-slate-100 text-xs text-slate-600 max-w-[250px] truncate"
                          title={row.ADDRESS}
                        >
                          {row.ADDRESS || "-"}
                        </td>
                        <td className="px-3 py-2 border-r border-slate-100 text-xs font-mono text-right text-slate-600">
                          {row.CRLIMIT ? Number(row.CRLIMIT).toFixed(2) : "-"}
                        </td>
                        <td className="px-3 py-2 border-r border-slate-100 text-xs font-mono text-slate-600">
                          {row.LPO || "-"}
                        </td>
                        <td className="px-3 py-2 text-xs font-mono text-slate-600">
                          {row.TRN || "-"}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={viewMore ? 13 : 9}
                  className="text-center py-12 text-slate-400"
                >
                  <Search className="w-10 h-10 text-slate-300 mb-2 mx-auto" />
                  <p className="text-base font-medium text-slate-700 mb-0.5">
                    No outstanding records found
                  </p>
                  <p className="text-xs text-slate-400">
                    Try adjusting your search criteria.
                  </p>
                </td>
              </tr>
            )}
          </tbody>

          {/* Sticky Footer */}
          <tfoot className="bg-gray-200 sticky bottom-0 z-20 shadow-lg border-t-2 border-slate-700">
            <tr>
              <td
                colSpan={4}
                className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-slate-300 border-r border-slate-800 text-right"
              >
                
              </td>
              <td className="px-3 py-3 text-right whitespace-nowrap font-mono border-r border-slate-800">
                <span className="block text-[10px] uppercase font-sans tracking-wide gray-700">
                  Total Debit
                </span>
                <span className="text-sm font-bold gray-700">
                  {debitTotal?.toFixed(2)}
                </span>
              </td>
              <td className="px-3 py-3 text-right whitespace-nowrap font-mono border-r border-slate-800">
                <span className="block text-[10px] uppercase font-sans tracking-wide gray-700">
                  Total Credit
                </span>
                <span className="text-sm font-bold gray-700">
                  {creditTotal?.toFixed(2)}
                </span>
              </td>
              <td className="px-3 py-3 text-right whitespace-nowrap font-mono ">
                <span className="block text-[10px] uppercase font-sans tracking-wide gray-700">
                  Total Balance
                </span>
                <span
                  className={`text-sm font-bold ${
                    debitTotal - creditTotal >= 0
                      ? "gray-700"
                      : "text-rose-400"
                  }`}
                >
                  {(debitTotal - creditTotal).toFixed(2)}
                </span>
              </td>
              {viewMore && (
                <td
                  className="bg-gray-200 border-l border-slate-800"
                  colSpan={4}
                ></td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default OutstandingTable;